import { Alert, Button } from "@mui/material";
import { useCallback, useEffect, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { resolveSession } from "../../features/auth/authSlice";
import { persistSession } from "../../features/auth/storage";
import { fetchAuthenticatedUser } from "../../lib/api/auth";

function parseStamp(value: string | null | undefined): number {
    if (!value) {
        return 0;
    }
    const ms = Date.parse(value);
    return Number.isNaN(ms) ? 0 : ms;
}

/**
 * Ако сървърът има по-нов permissionsUpdatedAt от сесията — банер за опресняване.
 */
export default function PermissionsReloadBanner() {
    const dispatch = useAppDispatch();
    const user = useAppSelector((state) => state.auth.user);
    const [needsReload, setNeedsReload] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const sessionStamp = user?.permissionsUpdatedAt ?? null;

    const checkServer = useCallback(async () => {
        if (!user) {
            setNeedsReload(false);
            return;
        }
        try {
            const fresh = await fetchAuthenticatedUser();
            if (parseStamp(fresh.permissionsUpdatedAt) > parseStamp(sessionStamp)) {
                setNeedsReload(true);
            }
        } catch {
            // тиха проверка
        }
    }, [user, sessionStamp]);

    useEffect(() => {
        void checkServer();
        const onFocus = () => {
            void checkServer();
        };
        window.addEventListener("focus", onFocus);
        const timer = window.setInterval(() => {
            void checkServer();
        }, 120_000);
        return () => {
            window.removeEventListener("focus", onFocus);
            window.clearInterval(timer);
        };
    }, [checkServer]);

    useEffect(() => {
        setNeedsReload(false);
    }, [sessionStamp]);

    if (!user || !needsReload) {
        return null;
    }

    const refresh = async () => {
        setRefreshing(true);
        try {
            const fresh = await fetchAuthenticatedUser();
            persistSession(fresh);
            dispatch(resolveSession(fresh));
            setNeedsReload(false);
        } finally {
            setRefreshing(false);
        }
    };

    return (
        <Alert
            severity="warning"
            sx={{ borderRadius: 0 }}
            action={
                <Button color="inherit" size="small" disabled={refreshing} onClick={() => void refresh()}>
                    {refreshing ? "Опресняване…" : "Опресни профила"}
                </Button>
            }
        >
            Правата са обновени — опресни профила
        </Alert>
    );
}
